import { SDKError } from '../../core/errors/SDKError.js';
import { ErrorCategory } from '../../core/errors/ErrorCategories.js';
import { ErrorCode } from '../../core/errors/ErrorCodes.js';

// Every backend-API failure, as one SDKError subclass (Milestone 7.3).
//
// Extends SDKError per the standing invariant - it carries a stable `code`
// from the shared catalog rather than inventing a parallel error system, so a
// publisher branches on `error.code` here exactly as they do for a VAST or
// playback failure.
//
// `status` (the HTTP status, when there was one) is assigned after super()
// like every other subclass's own fields. It is NOT folded into `context`,
// because context is sanitized and truncated while a status code is a small,
// always-safe integer callers legitimately switch on.
export class ApiError extends SDKError {
  constructor(message, { status = null, endpoint = null, ...options } = {}) {
    super(message, {
      code: ErrorCode.SDK_INTERNAL_ERROR,
      category: ErrorCategory.API,
      ...options,
    });

    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
  }
}

// A 401/403, or a token exchange that failed outright. Split out because it is
// the one API failure with a defined recovery action - re-authenticate - which
// AuthManager acts on and callers can detect without string matching.
export class ApiAuthError extends ApiError {
  constructor(message, options = {}) {
    super(message, { code: ErrorCode.SDK_API_AUTH_FAILED, ...options });
    this.name = 'ApiAuthError';
  }
}

// The backend answered, but the body was not the documented envelope - not
// JSON at all, or JSON missing `data`/`status`. Distinct from a 5xx: the
// transport worked, the contract did not hold.
export class ApiResponseError extends ApiError {
  constructor(message, options = {}) {
    super(message, { code: ErrorCode.SDK_API_MALFORMED_RESPONSE, ...options });
    this.name = 'ApiResponseError';
  }
}
